import { useState, useEffect } from "react";
import SEO from "../common/SEO";
import Layout from "../common/Layout";
import BreadcrumbOne from "../elements/breadcrumb/BreadcrumbOne";
import SectionTitle from "../elements/sectionTitle/SectionTitle";
import Separator from "../elements/separator/Separator";
import ScrollAnimation from "react-animate-on-scroll";
import { FiArrowRight, FiMapPin, FiPhone, FiMail, FiBriefcase, FiUsers, FiAward, FiTrendingUp } from "react-icons/fi";
import { BASE_URL, API_BASE_URL } from "../utils/api";

// Fallback openings shown when no job openings are returned by the API
const FallbackJobsData = [
  {
    id: "fallback-1",
    title: "Welder",
    location: "Coimbatore, Tamil Nadu",
    experience: "1-3 years",
    type: "Full-time",
    desc: "Perform MIG/arc welding and fabrication of cable trays, brackets and support structures, ensuring strong joints and adherence to quality standards."
  },
  {
    id: "fallback-2",
    title: "Design Engineer",
    location: "Coimbatore, Tamil Nadu",
    experience: "2-5 years",
    type: "Full-time",
    desc: "Prepare CAD drawings and design layouts for cable tray systems, panel boards and solar mounting structures, coordinating with production."
  },
  {
    id: "fallback-3",
    title: "Production Supervisor",
    location: "Coimbatore, Tamil Nadu",
    experience: "3-6 years",
    type: "Full-time",
    desc: "Supervise the shop floor fabrication team, plan daily production schedules for cable trays and solar mounting structures, and ensure quality and safety."
  }
];

// Fallback photos shown when no photos are returned by the API
const FallbackPhotosData = [
  {
    id: "fallback-photo-1",
    image: `${process.env.PUBLIC_URL}/images/careers/career-2.webp`,
    title: "Our Team",
    alt_text: "KR Power team at the facility"
  },
  {
    id: "fallback-photo-2",
    image: `${process.env.PUBLIC_URL}/images/careers/career-1.webp`,
    title: "On the Shop Floor",
    alt_text: "Team working on cable tray fabrication at KR Power"
  },
  {
    id: "fallback-photo-3",
    image: `${process.env.PUBLIC_URL}/images/careers/career-3.webp`,
    title: "Team at Work",
    alt_text: "KR Power team collaborating on the production floor"
  }
];

const Career = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    message: "",
    resume: null
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [jobs, setJobs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [visibleJobsCount, setVisibleJobsCount] = useState(3);
  const [visiblePhotosCount, setVisiblePhotosCount] = useState(4);
  const [loading, setLoading] = useState(true);

  const API_BASE = BASE_URL;
  const API_URL = API_BASE_URL;
  const FALLBACK_IMAGE = `${process.env.PUBLIC_URL || ''}/images/no-image.png`;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [openingsRes, photosRes] = await Promise.all([
          fetch(`${API_URL}/career/openings`),
          fetch(`${API_URL}/career/photos`)
        ]);
        if (cancelled) return;

        let jobsData = [];
        let photosData = [];
        try {
          if (openingsRes.ok) {
            const data = await openingsRes.json();
            jobsData = Array.isArray(data) ? data : [];
          }
        } catch (_) {
          // ignore - fallback openings will be used
        }
        try {
          if (photosRes.ok) {
            const data = await photosRes.json();
            photosData = Array.isArray(data) ? data : [];
          }
        } catch (_) {
          // ignore
        }
        if (cancelled) return;
        setJobs(jobsData.length > 0 ? jobsData.map(job => ({
          id: job.id,
          title: job.title,
          location: job.location,
          experience: job.experience,
          type: job.type,
          desc: job.description
        })) : FallbackJobsData);
        setPhotos(photosData.length > 0 ? photosData : FallbackPhotosData);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading career data:', err);
          setJobs(FallbackJobsData);
          setPhotos(FallbackPhotosData);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [API_URL]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return FALLBACK_IMAGE;
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('/images/')) return imagePath;
    if (imagePath.startsWith('/uploads/')) {
      return `${API_BASE}${imagePath}`;
    }
    return `${API_BASE}/uploads/career/${imagePath}`;
  };

  function handleChange(e) {
    const { name, value, files } = e.target;
    if (name === "phone") {
      setForm((s) => ({ ...s, phone: value.replace(/\D/g, '').slice(0, 10) }));
      return;
    }
    if (name === "resume") {
      const file = files[0];
      if (file && file.size > 1 * 1024 * 1024) {
        setSubmitError('Resume file is too large. Maximum size is 1MB.');
        e.target.value = '';
        setForm((s) => ({ ...s, resume: null }));
        setFileName("");
        return;
      }
      setSubmitError(null);
      setForm((s) => ({ ...s, resume: file }));
      setFileName(file ? file.name : "");
    } else {
      setForm((s) => ({ ...s, [name]: value }));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setSubmitted(false);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('position', form.position);
      if (form.message) {
        formData.append('message', form.message);
      }
      if (form.resume) {
        formData.append('resume', form.resume);
      }

      const response = await fetch(`${API_URL}/career/applications`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        // Success - reset form
        setForm({ name: "", email: "", phone: "", position: "", message: "", resume: null });
        setFileName("");
        setSubmitted(true);
        setSubmitError(null);
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        // Error from server
        setSubmitError(data.error || 'Failed to submit application. Please try again.');
        setSubmitted(false);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitError('Network error. Please check your connection and try again.');
      setSubmitted(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <SEO title="Careers || KR Power - Join Our Team" />
      <Layout>
        <BreadcrumbOne 
          title="Join Our Team of Power Innovators"
          rootUrl="/"
          parentUrl="Home"
          currentUrl="Careers"
        />
        
        <div className="main-content">
          {/* Hero Section */}
          <section className="career-hero rn-section-gap">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-8">
                  <div className="career-hero-content">
                    <ScrollAnimation 
                      animateIn="fadeInUp"
                      animateOut="fadeInOut"
                      animateOnce={true}>
                      <h1 className="career-hero-title">
                        Join Our Team of Power Innovators
                      </h1>
                    </ScrollAnimation>
                    <ScrollAnimation 
                      animateIn="fadeInUp"
                      animateOut="fadeInOut"
                      animateOnce={true}
                      delay={100}>
                      <p className="career-hero-description">
                        Build your career with KR Power Supports Pvt Ltd — where ideas light up the future. Explore
                        opportunities to grow, learn, and make an impact in the world of electrical
                        and solar systems.
                      </p>
                    </ScrollAnimation>
                    <ScrollAnimation 
                      animateIn="fadeInUp"
                      animateOut="fadeInOut"
                      animateOnce={true}
                      delay={200}>
                      <a href="#openings" className="btn-default btn-large round">
                        View Open Positions <i className="icon"><FiArrowRight /></i>
                      </a>
                    </ScrollAnimation>
                  </div>
                </div>
                <div className="col-lg-4">
                  <ScrollAnimation 
                    animateIn="fadeInRight"
                    animateOut="fadeInOut"
                    animateOnce={true}>
                    <div className="career-hero-image">
                      <div className="career-image-container">
                        <img 
                          src={`${process.env.PUBLIC_URL}/images/portfolio/kr.webp`}
                          alt="KR Power Supports Pvt Ltd — Projects & Control Systems in manufacturing solutions"
                          className="career-hero-img"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = e.target.nextSibling;
                            if (fallback) {
                              fallback.style.display = 'flex';
                            }
                          }}
                        />
                        <div className="career-image-fallback" style={{ display: 'none' }}>
                          <div className="career-image-content">
                            KR Power — Projects & Control Systems
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollAnimation>
                </div>
              </div>
            </div>
          </section>

          <Separator />

          {/* Why Work With Us */}
          <section className="career-why-us rn-section-gap">
            <div className="container">
              <div className="row">
                <div className="col-lg-12">
                  <SectionTitle
                    textAlign="text-center"
                    radiusRounded=""
                    subtitle="WHY JOIN US"
                    title="Why Work With KR Power Supports Pvt Ltd?"
                    description="We value people who want to build reliable power systems and sustainable solar solutions. Join a team that invests in your growth and safety."
                  />
                </div>
              </div>
              <div className="career-why-card">
                <div className="row">
                  <div className="col-lg-6">
                    <ScrollAnimation 
                      animateIn="fadeInLeft"
                      animateOut="fadeInOut"
                      animateOnce={true}>
                      <div className="career-benefits-wrapper">
                        <ul className="career-benefits-list">
                          <li>
                            <FiTrendingUp className="benefit-icon" />
                            <span>Growth opportunities on large projects</span>
                          </li>
                          <li>
                            <FiUsers className="benefit-icon" />
                            <span>Collaborative and supportive team culture</span>
                          </li>
                          <li>
                            <FiAward className="benefit-icon" />
                            <span>Hands-on experience with advanced power technologies</span>
                          </li>
                          <li>
                            <FiBriefcase className="benefit-icon" />
                            <span>Strong emphasis on safety and quality</span>
                          </li>
                        </ul>
                      </div>
                    </ScrollAnimation>
                  </div>
                  <div className="col-lg-6">
                    <ScrollAnimation 
                      animateIn="fadeInRight"
                      animateOut="fadeInOut"
                      animateOnce={true}>
                      <div className="career-stats-grid">
                        <div className="career-stat-card">
                          <div className="career-stat-icon">
                            <FiBriefcase />
                          </div>
                          <div className="career-stat-number">120+</div>
                          <div className="career-stat-label">Projects Delivered</div>
                        </div>
                        <div className="career-stat-card">
                          <div className="career-stat-icon">
                            <FiAward />
                          </div>
                          <div className="career-stat-number">50+</div>
                          <div className="career-stat-label">Panel Types</div>
                        </div>
                        <div className="career-stat-card">
                          <div className="career-stat-icon">
                            <FiUsers />
                          </div>
                          <div className="career-stat-number">200+</div>
                          <div className="career-stat-label">Team Members</div>
                        </div>
                        <div className="career-stat-card">
                          <div className="career-stat-icon">
                            <FiTrendingUp />
                          </div>
                          <div className="career-stat-number">10+</div>
                          <div className="career-stat-label">Years Experience</div>
                        </div>
                      </div>
                    </ScrollAnimation>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Separator />

          {/* Current Openings */}
          <section id="openings" className="career-openings rn-section-gap">
            <div className="container">
              <div className="row">
                <div className="col-lg-12">
                  <SectionTitle
                    textAlign="text-center"
                    radiusRounded=""
                    subtitle="CAREER OPPORTUNITIES"
                    title="Current Openings"
                    description="Explore exciting career opportunities and join our team of professionals."
                  />
                </div>
              </div>
              {loading ? (
                <div className="text-center" style={{ padding: '50px 0' }}>
                  <p>Loading openings...</p>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center" style={{ padding: '50px 0' }}>
                  <p style={{ color: 'var(--color-body)', fontSize: '18px' }}>No job openings available at the moment.</p>
                  <p style={{ color: 'var(--color-body)', fontSize: '14px', marginTop: '10px', opacity: 0.7 }}>
                    Please check back later or contact us for opportunities.
                  </p>
                </div>
              ) : (
                <>
                  <div className="row">
                    {jobs.slice(0, visibleJobsCount).map((job, index) => (
                      <div key={job.id} className="col-lg-4 col-md-6 mt--30">
                        <ScrollAnimation 
                          animateIn="fadeInUp"
                          animateOut="fadeInOut"
                          animateOnce={true}
                          delay={index * 100}>
                          <div className="career-job-card">
                            <div className="career-job-header">
                              <FiBriefcase className="career-job-icon" />
                              <h4 className="career-job-title">{job.title}</h4>
                            </div>
                            <div className="career-job-meta">
                              <span className="career-job-location">
                                <FiMapPin /> {job.location}
                              </span>
                              <span className="separator">•</span>
                              <span className="career-job-type">{job.type}</span>
                            </div>
                            <div className="career-job-experience">
                              <strong>Experience:</strong> {job.experience}
                            </div>
                            <p className="career-job-desc">{job.desc}</p>
                            <div className="career-job-actions">
                              <a 
                                href="#apply" 
                                onClick={(e) => { 
                                  e.preventDefault(); 
                                  // Update form state with selected position
                                  setForm(prev => ({ ...prev, position: job.title }));
                                  // Scroll to apply section
                                  setTimeout(() => {
                                    const applySection = document.getElementById('apply');
                                    if (applySection) {
                                      window.scrollTo({ top: applySection.offsetTop - 100, behavior: 'smooth' });
                                    }
                                  }, 100);
                                }} 
                                className="btn-default btn-small round"
                              >
                                Apply Now <i className="icon"><FiArrowRight /></i>
                              </a>
                            </div>
                          </div>
                        </ScrollAnimation>
                      </div>
                    ))}
                  </div>
                  {jobs.length > 3 && visibleJobsCount < jobs.length && (
                    <div className="text-center mt--40">
                      <ScrollAnimation 
                        animateIn="fadeInUp"
                        animateOut="fadeInOut"
                        animateOnce={true}>
                        <button
                          onClick={() => setVisibleJobsCount(jobs.length)}
                          className="btn-default btn-small round"
                        >
                          Load More Positions <i className="icon"><FiArrowRight /></i>
                        </button>
                      </ScrollAnimation>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          <Separator />

          {/* Life at KR Power */}
          <section className="career-life rn-section-gap">
            <div className="container">
              <div className="row">
                <div className="col-lg-12">
                  <SectionTitle
                    textAlign="text-center"
                    radiusRounded=""
                    subtitle="WORKPLACE CULTURE"
                    title="Life at KR Power Supports Pvt Ltd"
                    description="A day on-site — teamwork, learning, and hands-on work with power systems."
                  />
                </div>
              </div>
              <div className="career-life-card">
                {photos.length === 0 ? (
                  <div className="text-center" style={{ padding: '50px 0' }}>
                    <p style={{ color: 'var(--color-body)', fontSize: '18px' }}>No photos available at the moment.</p>
                    <p style={{ color: 'var(--color-body)', fontSize: '14px', marginTop: '10px', opacity: 0.7 }}>
                      Photos will be displayed here once they are added.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="career-life-team">
                      <div className="career-gallery-item">
                        <div className="career-gallery-card">
                          <img
                            src={getImageUrl(photos[0].image)}
                            alt={photos[0].alt_text || photos[0].title || 'Life at KR Power'}
                            className="career-gallery-image"
                            onError={(e) => {
                              if (!e.target.dataset.fallbackUsed) {
                                e.target.dataset.fallbackUsed = 'true';
                                e.target.src = FALLBACK_IMAGE;
                              }
                            }}
                          />
                          <div className="career-gallery-overlay">
                            <div className="career-gallery-label">{photos[0].title || 'Life at KR Power'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {photos.length > 1 && (
                      <div className="career-life-grid">
                        {photos.slice(1, visiblePhotosCount).map((photo) => (
                          <div key={photo.id} className="career-gallery-item">
                            <div className="career-gallery-card">
                              <img
                                src={getImageUrl(photo.image)}
                                alt={photo.alt_text || photo.title || 'Life at KR Power'}
                                className="career-gallery-image"
                                onError={(e) => {
                                  if (!e.target.dataset.fallbackUsed) {
                                    e.target.dataset.fallbackUsed = 'true';
                                    e.target.src = FALLBACK_IMAGE;
                                  }
                                }}
                              />
                              <div className="career-gallery-overlay">
                                <div className="career-gallery-label">{photo.title || 'Life at KR Power'}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {photos.length > 4 && visiblePhotosCount < photos.length && (
                      <div className="text-center mt--40">
                        <ScrollAnimation
                          animateIn="fadeInUp"
                          animateOut="fadeInOut"
                          animateOnce={true}>
                          <button
                            onClick={() => setVisiblePhotosCount(photos.length)}
                            className="btn-default btn-small round"
                          >
                            Load More Photos <i className="icon"><FiArrowRight /></i>
                          </button>
                        </ScrollAnimation>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>

          <Separator />

          {/* Apply Form */}
          <section id="apply" className="career-apply rn-section-gap">
            <div className="container">
              <div className="row">
                <div className="col-lg-12 mb--40">
                  <SectionTitle
                    textAlign="text-center"
                    radiusRounded=""
                    subtitle="JOIN OUR TEAM"
                    title="Apply Now"
                    description="Submit your application and our HR team will review it."
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-7">
                  <ScrollAnimation 
                    animateIn="fadeInLeft"
                    animateOut="fadeInOut"
                    animateOnce={true}>
                    <div className="career-form-card">
                    <form onSubmit={handleSubmit} className="career-form">
                      <div className="career-form-group">
                        <label className="career-form-label">Full Name *</label>
                        <input 
                          required 
                          name="name" 
                          value={form.name} 
                          onChange={handleChange} 
                          className="career-form-input"
                          type="text"
                        />
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="career-form-group">
                            <label className="career-form-label">Email *</label>
                            <input 
                              required 
                              name="email" 
                              value={form.email} 
                              onChange={handleChange} 
                              className="career-form-input"
                              type="email"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="career-form-group">
                            <label className="career-form-label">Phone *</label>
                            <input
                              required
                              name="phone"
                              value={form.phone}
                              onChange={handleChange}
                              className="career-form-input"
                              type="tel"
                              pattern="[0-9]{10}"
                              maxLength={10}
                              title="Please enter a valid 10-digit phone number"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="career-form-group">
                        <label className="career-form-label">Position Applying For</label>
                        <div className="career-select-wrapper">
                          <select 
                            id="apply-position" 
                            name="position" 
                            value={form.position} 
                            onChange={handleChange} 
                            className="career-form-select"
                          >
                            <option value="">Select position</option>
                            {jobs.map((j) => (
                              <option key={j.id} value={j.title}>{j.title}</option>
                            ))}
                          </select>
                          <span className="career-select-arrow"></span>
                        </div>
                      </div>
                      <div className="career-form-group">
                        <label className="career-form-label">Resume (PDF or DOC, max 1MB)</label>
                        <div className="career-file-wrapper">
                          <input 
                            name="resume" 
                            type="file" 
                            accept=".pdf,.doc,.docx" 
                            onChange={handleChange} 
                            className="career-form-file"
                            id="resume-upload"
                          />
                          <label htmlFor="resume-upload" className="career-file-label">
                            <span className="career-file-icon">📄</span>
                            <span className="career-file-text">
                              {fileName || "Choose file or drag it here"}
                            </span>
                            <span className="career-file-button">Browse</span>
                          </label>
                          {fileName && (
                            <button
                              type="button"
                              onClick={() => {
                                setForm((s) => ({ ...s, resume: null }));
                                setFileName("");
                                document.getElementById('resume-upload').value = '';
                              }}
                              className="career-file-remove"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="career-form-group">
                        <label className="career-form-label">Message / Cover Letter</label>
                        <textarea 
                          name="message" 
                          value={form.message} 
                          onChange={handleChange} 
                          rows="4" 
                          className="career-form-textarea"
                        />
                      </div>
                      <div className="career-form-group">
                        <button 
                          type="submit" 
                          className="btn-default btn-large round"
                          disabled={submitting}
                        >
                          {submitting ? 'Submitting...' : 'Submit Application'} <i className="icon"><FiArrowRight /></i>
                        </button>
                        {submitted && (
                          <span className="career-success-message" style={{ 
                            display: 'block', 
                            marginTop: '10px', 
                            color: '#28a745', 
                            fontWeight: '600' 
                          }}>
                            ✓ Application submitted successfully! We will review your application soon.
                          </span>
                        )}
                        {submitError && (
                          <span className="career-error-message" style={{ 
                            display: 'block', 
                            marginTop: '10px', 
                            color: '#dc3545', 
                            fontWeight: '600' 
                          }}>
                            ✗ {submitError}
                          </span>
                        )}
                      </div>
                    </form>
                  </div>
                  </ScrollAnimation>
                </div>
                <div className="col-lg-5">
                  <ScrollAnimation 
                    animateIn="fadeInRight"
                    animateOut="fadeInOut"
                    animateOnce={true}>
                    <div className="career-sidebar-card">
                      <h4 className="career-sidebar-title">
                        <FiBriefcase className="sidebar-icon" />
                        Recruitment Process
                      </h4>
                      <ol className="career-process-list">
                        <li>Screening of resume</li>
                        <li>Technical interview</li>
                        <li>On-site or virtual assessment</li>
                        <li>Offer and onboarding</li>
                      </ol>
                      <div className="career-contact-info">
                        <h5 className="career-contact-title">
                          <FiUsers className="sidebar-icon" />
                          Contact HR
                        </h5>
                        <div className="career-contact-detail">
                          <FiMail className="contact-icon" />
                          <span>info@krpower.com</span>
                        </div>
                        <div className="career-contact-detail">
                          <FiPhone className="contact-icon" />
                          <span>+91 98765 43210</span>
                        </div>
                      </div>

                      {/* <div className="career-recent-applications">
                        <h5 className="career-contact-title">
                          <FiTrendingUp className="sidebar-icon" />
                          Recent Applications
                        </h5>
                        <div className="career-applications-list">
                          {applications.length === 0 ? (
                            <div className="career-no-applications">No recent applications</div>
                          ) : (
                            applications.map((a) => (
                              <div key={a.id} className="career-application-item">
                                {a.name} — {a.position || '—'}
                              </div>
                            ))
                          )}
                        </div>
                      </div> */}
                      
                    </div>
                  </ScrollAnimation>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
};

export default Career;

